const OMEMO_PARTNER_CODE_STORAGE_KEY = "omemo_partner_code_first_touch";
const OMEMO_PARTNER_COOKIE_KEY = "omemo_partner_code";
const OMEMO_PARTNER_COOKIE_TTL_SECONDS = 90 * 24 * 60 * 60;
const OMEMO_PARTNER_QUERY_KEYS = [
  "ref",
  "promo_code",
  "omemo_partner_code",
] as const;

function isBrowserEnvironment(): boolean {
  return typeof window !== "undefined";
}

/**
 * Trim only — do not lowercase. Omemo matches codes case-insensitively,
 * but we preserve the original first-touch value in NIAP storage.
 */
export function normalizeOmemoPartnerCode(
  value?: string | null,
): string | undefined {
  if (value == null) {
    return undefined;
  }

  const normalized = value.trim();
  return normalized !== "" ? normalized : undefined;
}

export function extractOmemoPartnerCodeFromSearchParams(
  searchParams: URLSearchParams,
): string | undefined {
  for (const key of OMEMO_PARTNER_QUERY_KEYS) {
    const code = normalizeOmemoPartnerCode(searchParams.get(key));
    if (code) {
      return code;
    }
  }

  return undefined;
}

function resolveCookieDomain(hostname: string): string {
  if (hostname === "nplanner.ru" || hostname.endsWith(".nplanner.ru")) {
    return "; Domain=.nplanner.ru";
  }

  return "";
}

function readOmemoPartnerCodeFromCookie(): string | undefined {
  if (!isBrowserEnvironment()) {
    return undefined;
  }

  const prefix = `${OMEMO_PARTNER_COOKIE_KEY}=`;
  const cookie = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  if (!cookie) {
    return undefined;
  }

  try {
    return normalizeOmemoPartnerCode(
      decodeURIComponent(cookie.slice(prefix.length)),
    );
  } catch {
    return undefined;
  }
}

function writeOmemoPartnerCodeCookie(partnerCode: string): void {
  if (!isBrowserEnvironment()) {
    return;
  }

  const encoded = encodeURIComponent(partnerCode);
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const domain = resolveCookieDomain(window.location.hostname);
  document.cookie = `${OMEMO_PARTNER_COOKIE_KEY}=${encoded}; Path=/; Max-Age=${OMEMO_PARTNER_COOKIE_TTL_SECONDS}; SameSite=Lax${secure}${domain}`;
}

function readOmemoPartnerCodeFromLocalStorage(): string | undefined {
  if (!isBrowserEnvironment()) {
    return undefined;
  }

  try {
    return normalizeOmemoPartnerCode(
      window.localStorage.getItem(OMEMO_PARTNER_CODE_STORAGE_KEY),
    );
  } catch {
    return undefined;
  }
}

function writeOmemoPartnerCodeToLocalStorage(partnerCode: string): void {
  if (!isBrowserEnvironment()) {
    return;
  }

  try {
    window.localStorage.setItem(OMEMO_PARTNER_CODE_STORAGE_KEY, partnerCode);
  } catch {
    // noop — private mode / quota
  }
}

function persistOmemoPartnerCode(partnerCode: string): void {
  writeOmemoPartnerCodeToLocalStorage(partnerCode);
  writeOmemoPartnerCodeCookie(partnerCode);
}

/**
 * First-touch resolution order: URL → localStorage → cookie.
 * Already stored value is never overwritten.
 */
export function readOmemoPartnerCode(): string | undefined {
  if (!isBrowserEnvironment()) {
    return undefined;
  }

  const fromStorage = readOmemoPartnerCodeFromLocalStorage();
  if (fromStorage) {
    writeOmemoPartnerCodeCookie(fromStorage);
    return fromStorage;
  }

  const fromCookie = readOmemoPartnerCodeFromCookie();
  if (fromCookie) {
    writeOmemoPartnerCodeToLocalStorage(fromCookie);
    return fromCookie;
  }

  return undefined;
}

/**
 * Lifetime first-touch: capture ?ref= (aliases: promo_code, omemo_partner_code)
 * once and never overwrite an existing stored value.
 * Syncs the resolved value into localStorage + cookie.
 */
export function captureOmemoPartnerCodeFromUrl(
  search: string = typeof window !== "undefined" ? window.location.search : "",
): string | undefined {
  if (!isBrowserEnvironment()) {
    return undefined;
  }

  const existing = readOmemoPartnerCode();
  if (existing) {
    persistOmemoPartnerCode(existing);
    return existing;
  }

  const normalizedSearch = search.startsWith("?") ? search.slice(1) : search;
  const fromUrl = extractOmemoPartnerCodeFromSearchParams(
    new URLSearchParams(normalizedSearch),
  );

  if (!fromUrl) {
    return undefined;
  }

  persistOmemoPartnerCode(fromUrl);
  return fromUrl;
}

export function getOmemoPartnerCodeForRegistration(): string {
  return captureOmemoPartnerCodeFromUrl() ?? readOmemoPartnerCode() ?? "";
}

/** Exported for unit tests. */
export const __testables = {
  OMEMO_PARTNER_COOKIE_KEY,
  OMEMO_PARTNER_COOKIE_TTL_SECONDS,
  resolveCookieDomain,
  readOmemoPartnerCodeFromCookie,
  writeOmemoPartnerCodeCookie,
};
